/**
 * The card parts that read or decide a choice through the agent (its store, its socket). The rest
 * (CardParts) needs neither, so the MCP view (ui://steddy/decision) builds without them.
 */
import { Button } from "@/components/ui/button";

import type { ToolArtifact } from "../../aui/convert";
import { ApprovalCard } from "../../choices/ApprovalCard";
import { choiceOptions, isDeclineOption } from "../../choices/wire";
import type { WireChoice } from "../../protocol/protocol";
import type { CardEdits } from "./overrides";
import { useDecide } from "./useDecide";

/**
 * What a native card draws when its payload is not one it can read (a malformed approval card):
 * the call's generic approval card, as a call with no native card gets.
 */
export function GenericApproval({ artifact }: { artifact: ToolArtifact }) {
  if (artifact.choiceKind !== "approval" || !artifact.choiceRef) return null;
  return <ApprovalCard choiceRef={artifact.choiceRef} />;
}

/**
 * A waiting write's options as the footer's buttons ("Save terms" / "Don't save"), with why the
 * last press failed or that it redrew the card. `edits`: what the approve sends as the user's
 * changes on the card.
 */
export function ChoiceButtons({
  choice,
  edits,
}: {
  choice: WireChoice;
  edits?: () => CardEdits | undefined;
}) {
  const { decide, pressed, error, redrawn } = useDecide(choice.ref, choice.args_sha256);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {choiceOptions(choice).map((option) => (
        <Button
          key={option.id}
          type="button"
          size="sm"
          variant={isDeclineOption(option.id) ? "outline" : "default"}
          disabled={pressed !== null}
          onClick={() => void decide(option.id, isDeclineOption(option.id) ? undefined : edits?.())}
        >
          {option.label}
        </Button>
      ))}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {redrawn && (
        <p role="status" className="text-xs font-medium text-primary">
          {redrawn}
        </p>
      )}
    </div>
  );
}
